import { createContext, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { displayError, displayErrorExpired, displaySuccess } from '../utils/messageToast';
import { LoggedInUserProps, AuthContextData, SignInProps, AuthProviderProps } from '../utils/functionsInterfaces';
import { SetupNotAuth } from '../apis/apiNotAuthenticate';

export const AUTH_KEYS = {
  AUTHORIZATION: 'Authorization',
  AUTHORIZATION_RA: 'AuthorizationRA',
  AUTHORIZATION_FORCE: 'Authorization_force',
  CLIENT_ID_FORCE: 'clientIdForce',
  SESSION: 'SESSION_KEY',
  LOGOUT_TIMEOUT: 'LOGOUT_TIMEOUT_KEY',
  SLUG: 'slug',
  AMBIENT: 'ambient_to',
  IN_FORCE: 'inForce',
} as const;

export const AuthContext = createContext({} as AuthContextData);

export function AuthProvider({ children }: AuthProviderProps) {
  const navigate = useNavigate();

  const [authorization, setAuthorization] = useState('');
  const [authorizationRA, setAuthorizationRA] = useState('');
  const [authorizationForce, setAuthorizationForce] = useState('');
  const [user, setUser] = useState<LoggedInUserProps | null>(null);
  const [manualSignOut, setManualSignOut] = useState(false);

  const setupAutoLogout = (timeoutMs: number) => {
    const expiration = Date.now() + timeoutMs;
    localStorage.setItem(AUTH_KEYS.LOGOUT_TIMEOUT, expiration.toString());
  };

  const performSignOut = (expired = false) => {
    if (!manualSignOut) {
      localStorage.clear();
      setAuthorization('');
      setAuthorizationRA('');
      setAuthorizationForce('');
      setUser(null);
      navigate('/');
      expired ? displayErrorExpired('Sessão expirada!') : "";
    }
  };

  const restoreSession = () => {
    const isLoginPage = window.location.pathname === '/';
    let rcForce;
    if (isLoginPage) return;

    const storedAuth = localStorage.getItem(AUTH_KEYS.AUTHORIZATION);
    const storedRA = localStorage.getItem(AUTH_KEYS.AUTHORIZATION_RA);
    const storedForce = localStorage.getItem(AUTH_KEYS.AUTHORIZATION_FORCE);
    const storedUser = localStorage.getItem(AUTH_KEYS.SESSION);
    const logoutTime = localStorage.getItem(AUTH_KEYS.LOGOUT_TIMEOUT);

    if (!storedAuth || !storedRA || !storedUser || !logoutTime || Date.now() > Number(logoutTime)) {
      performSignOut(true);
      return;
    }

    setAuthorization(storedAuth);
    setAuthorizationRA(storedRA);
    setAuthorizationForce(storedForce || '');
    setUser(JSON.parse(storedUser));
  };

  useEffect(() => {
    restoreSession();

    const intervalId = setInterval(() => {
      const timeout = localStorage.getItem(AUTH_KEYS.LOGOUT_TIMEOUT);
      if (timeout && Date.now() > Number(timeout)) {
        clearInterval(intervalId);
        performSignOut(true);
      }
    }, 1000);

    return () => clearInterval(intervalId);
  }, []);

  const signIn = async ({ slug, inForce, ambient }: SignInProps) => {
    try {
      const api = SetupNotAuth();
      const { data } = await api.post('/login-tech', { slug, inForce: String(inForce) });

      const userData: LoggedInUserProps = {
        clientName: inForce ? data.company.name : data.company.clientName,
        clientId: data.company.id,
        userName: inForce ? data.company.name : data.company.clientName,//,
        ambient,
        userEmail: data.user.email,
        slug,
        loginTimestamp: Date.now(),
        ambientLogged: ambient,
        inForce,
      };

      setUser(userData);
      setAuthorization(data.tokens.Authorization);
      setAuthorizationRA(data.tokens.AuthorizationRA);
      setAuthorizationForce(data.tokens.AuthorizationForce);

      localStorage.setItem(AUTH_KEYS.AUTHORIZATION, data.tokens.Authorization);
      localStorage.setItem(AUTH_KEYS.AUTHORIZATION_RA, data.tokens.AuthorizationRA);
      localStorage.setItem(AUTH_KEYS.AUTHORIZATION_FORCE, data.tokens.AuthorizationForce || '');
      localStorage.setItem(AUTH_KEYS.CLIENT_ID_FORCE, data.company.tenant_id || '');
      localStorage.setItem(AUTH_KEYS.SLUG, slug);
      localStorage.setItem(AUTH_KEYS.AMBIENT, ambient);
      localStorage.setItem(AUTH_KEYS.IN_FORCE, String(inForce));
      localStorage.setItem(AUTH_KEYS.SESSION, JSON.stringify(userData));

      setupAutoLogout(1 * 60 * 60 * 1000); // 1 hora

      const clientId = inForce ? data.company.tenant_id : data.company.id;
      displaySuccess('Login efetuado com sucesso!');
      navigate(`/section/listForms?clientId=${clientId}`);
    } catch (err) {
      displayError('Erro ao fazer login!');
    }
  };

  const signOut = () => {
    setManualSignOut(true);
    performSignOut();
  };

  return (
    <AuthContext.Provider value={{ signIn, signOut, authorizationRA, authorization, user, authorizationForce }}>
      {children}
    </AuthContext.Provider>
  );
}
